import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
} from "@mui/material";
import { useDepartments } from "../hooks";
import type { Department } from "../api/types";

interface DepartmentSelectSingleProps {
  value: string | null;
  onChange: (departmentId: string | null) => void;
  onDepartmentChange?: (department: Department | null) => void;
  label?: string;
  placeholder?: string;
  fullWidth?: boolean;
}

export const DepartmentSelectSingle: React.FC<DepartmentSelectSingleProps> = ({
  value,
  onChange,
  onDepartmentChange,
  label = "Select Department",
  placeholder = "Search and select a department...",
  fullWidth = true,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedDepartmentData, setSelectedDepartmentData] =
    useState<Department | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, error } = useDepartments();

  const departments = useMemo(() => {
    const options = data?.data || [];
    if (!debouncedSearch) return options;
    return options.filter((dept) =>
      dept.name.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [data, debouncedSearch]);

  const selectedDepartment = useMemo(() => {
    if (!value) return null;
    if (selectedDepartmentData?.id === value) return selectedDepartmentData;
    return departments.find((d) => d.id === value) ?? null;
  }, [selectedDepartmentData, departments, value]);

  const options = useMemo(() => {
    if (!selectedDepartmentData) return departments;
    const rest = departments.filter(
      (d) => d.id !== selectedDepartmentData.id
    );
    return [selectedDepartmentData, ...rest];
  }, [selectedDepartmentData, departments]);

  useEffect(() => {
    if (!value) {
      setSelectedDepartmentData(null);
      onDepartmentChange?.(null);
      return;
    }
    if (selectedDepartmentData?.id === value) return;
    const found = departments.find((d) => d.id === value);
    if (found) {
      setSelectedDepartmentData(found);
      onDepartmentChange?.(found);
    }
  }, [onDepartmentChange, selectedDepartmentData?.id, departments, value]);

  if (error) {
    return (
      <Alert severity="error">
        Failed to load departments. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      <Autocomplete
        fullWidth={fullWidth}
        options={options}
        value={selectedDepartment}
        onChange={(_event, newValue: Department | null) => {
          setSelectedDepartmentData(newValue);
          onDepartmentChange?.(newValue);
          onChange(newValue?.id ?? null);
        }}
        inputValue={searchTerm}
        onInputChange={(_event, newInputValue) => setSearchTerm(newInputValue)}
        getOptionLabel={(option) => option.name}
        loading={isLoading}
        filterOptions={(x) => x}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            size="small"
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
};
